import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Pathway from './pathway';
import Validator from '../../../../util/validator';
import {useStylesECDS} from './ecdsStyle';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import {hasPermission, Permissions, user} from '../../../../store/reducers/user';
import _ from 'lodash';
import API from '../../../../app/api';
import ObsTestEdit from './ObsTestEdit';
import {addNoteForPatient} from '../../../../store/reducers/notes';
import Notes from '../../../../util/notes';
import Formatter from '../../../../util/formatter';
import {setSnackbar} from '../../../../store/reducers/globals';
import useOpenState from '../../../../hooks/useOpenState';
import AgyleAccordion from '../../../../components/ui/accordion/agyleAccordion';
import config from '../../../../app/config';
import Identity from './identity';
import Characteristics from './characteristics';
import MentalHealth from './mentalHealth';
import GpRegistration from './gpRegistration';
import AttendanceLocation from './attendanceLocation';
import AmbulanceDetails from './ambulanceDetails';
import TimeOfTreatment from './timeOfTreatment';
import CodedScoredAssessment from './codedScoredAssessment';
import CodedClinicalObservations from './codedClinicalObservations';
import CodedClinicalFindings from './codedClinicalFindings';
import Injury from './injury';
import PatientHistory from './patientHistory';
import AgreementServiceDetails from './agreementServiceDetails.jsx';
import CareProfessionals from './careProfessionals';
import Diagnosis from './diagnosis';
import Investigations from './investigations';
import Treatments from './treatments';
import DiseaseOutbreak from './diseaseOutbreak';
import DischargeECDS from './dischargeECDS';
import ReferralsOtherServices from './referralsOtherServices';
import {getDictForECDS, getEcdsVersion} from '../../../../store/reducers/dicts';
import {FormLabel} from '@mui/material';
import AttendanceActivity from './attendanceActivity';
import SocialAndPersonalCircumstances from './socialAndPersonalCircumstances';
import EMed3ECDS from './eMed3ECDS';

const EcdsForm = (props) => {
    const {t} = useTranslation();
    const classes = useStylesECDS();
    const dispatch = useDispatch();

    // User
    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    const controller = new AbortController();

    // Form control
    const [steps, setSteps] = useState([]);
    const versionECDS = dispatch(getEcdsVersion(props.patient?.admission?.startDate));

    // Accordion
    const [expanded, setExpanded] = useState(false);

    // Components Data Set
    const [patientPathway, setPatientPathway] = useState({});
    const [patientIdentity, setPatientIdentity] = useState({});
    const [patientCharacteristics, setPatientCharacteristics] = useState({});
    const [mentalHealth, setMentalHealth] = useState([]);
    const [gp, setGP] = useState({});
    const [attendanceLocation, setAttendanceLocation] = useState({});
    const [ambulanceDetails, setAmbulanceDetails] = useState({});
    const [attendanceActivity, setAttendanceActivity] = useState({});
    const [timeOfTreatment, setTimeOfTreatment] = useState({});
    const [assessment, setAssessment] = useState({});
    const [observations, setObservations] = useState({});
    const [findings, setFindings] = useState({});
    const [injury, setInjury] = useState({});
    const [history, setHistory] = useState({});
    const [agreement, setAgreement] = useState({});
    const [care, setCare] = useState({});
    const [diagnosis, setDiagnosis] = useState({});
    const [investigations, setInvestigations] = useState({});
    const [treatments, setTreatments] = useState({});
    const [otherService, setOtherService] = useState({});
    const [discharge, setDischarge] = useState({});
    const [outbreak, setOutbreak] = useState({});
    const [social, setSocial] = useState({});
    const [eMed, setEMed] = useState({});

    // Mandatory fields
    const [mandatory, setMandatory] = useState({});

    // Observations
    const [observation, setObservation] = useState([]);
    const [selectedObs, setSelectedObs] = useState(null);
    const [isObsEditorOpen, openObsEditor, closeObsEditor] = useOpenState(false, true);

    // Others
    const [isSmall, setIsSmall] = useState(false);

    useEffect(() => {
        setIsSmall(props._containerWidth < 730)
    }, [props._containerWidth]);

    useEffect(() => {
        getSteps(versionECDS);
    }, [versionECDS, patientPathway,
        patientIdentity,
        patientCharacteristics,
        mentalHealth,
        gp,
        attendanceLocation,
        ambulanceDetails,
        attendanceActivity,
        timeOfTreatment,
        assessment,
        observations,
        findings,
        injury,
        history,
        agreement,
        care,
        diagnosis,
        investigations,
        treatments,
        otherService,
        discharge,
        outbreak,
        isSmall,
    ]);

    const validation = (min, max, data) => {
        return Validator.validEcdsFormLenght(min, max, data);
    };

    useEffect(() => {
        updateObs();
        return () => {
            controller.abort();
        };
    }, []);

    useEffect(() => {
        if (props.onSubmitFunction) {
            props.onSubmitFunction(submit);
        }
    }, [
        props.patient,
        props.patientAgeType,
        patientPathway,
        patientIdentity,
        patientCharacteristics,
        mentalHealth,
        gp,
        attendanceLocation,
        ambulanceDetails,
        attendanceActivity,
        timeOfTreatment,
        assessment,
        observations,
        findings,
        history,
        injury,
        agreement,
        care,
        diagnosis,
        investigations,
        treatments,
        otherService,
        discharge,
        outbreak,
        social,
        eMed,
        versionECDS,
    ]);

    const handleEditObs = (id) => {
        setSelectedObs(id);
        openObsEditor();
    };

    const getSteps = (version) => {
        // Components
        let accordionSteps = [
            {
                label: t('Patient Identity'),
                component:
                    <Identity
                        setData={setPatientIdentity}
                        data={patientIdentity}
                        patient={props.patient}
                        checkLenght={validation}
                        valid={setMandatory}
                        isSmall={isSmall}
                    />,
            },
            {
                label: t('Patient Characteristics'),
                component:
                    <Characteristics
                        setData={setPatientCharacteristics}
                        patient={props.patient}
                        checkLenght={validation}
                        valid={setMandatory}
                        isSmall={isSmall}
                    />,
            },
            {
                label: t('Mental Health Act Legal Status'),
                component:
                    <MentalHealth
                        setData={setMentalHealth}
                        patient={props.patient}
                        checkLenght={validation}
                        valid={setMandatory}
                        ecdsData
                        isSmall={isSmall}
                    />,
            },
            {
                label: t('General Medical Registration'),
                component:
                    <GpRegistration
                        setData={setGP}
                        patient={props.patient}
                        checkLenght={validation}
                        valid={setMandatory}
                        isSmall={isSmall}
                    />,
            },
            {
                label: t(version === 'V3'
                    ? 'Emergency Care Attendance Location'
                    : 'Urgent And Emergency Care Attendance Location'),
                component:
                    <AttendanceLocation
                        setData={setAttendanceLocation}
                        patient={props.patient}
                        checkLenght={validation}
                        valid={setMandatory}
                        isSmall={isSmall}
                    />,
            },
            {
                label: t('Ambulance Details'),
                component:
                    <AmbulanceDetails
                        setData={setAmbulanceDetails}
                        patient={props.patient}
                        checkLenght={validation}
                        valid={setMandatory}
                        isSmall={isSmall}
                    />,
            },
            {
                label: t(version === 'V3'
                    ? 'Emergency Care Attendance Activity Characteristics'
                    : 'Urgent And Emergency Care Attendance Activity Characteristics'),
                component:
                    <AttendanceActivity
                        setData={setAttendanceActivity}
                        patient={props.patient}
                        checkLenght={validation}
                        valid={setMandatory}
                        accordion={expanded}
                        isSmall={isSmall}
                    />,
            },
            {
                label: t('Expected Time of Treatment'),
                component:
                    <TimeOfTreatment
                        setData={setTimeOfTreatment}
                        patient={props.patient}
                        checkLenght={validation}
                        valid={setMandatory}
                    />,
            },
            {
                label: t('Scored Assessment'),
                component:
                    <CodedScoredAssessment
                        setData={setAssessment}
                        patient={props.patient}
                        checkLenght={validation}
                        valid={setMandatory}
                        obs={observation}
                        editObs={handleEditObs}
                    />,
            },
            {
                label: t('Clinical Observations'),
                component:
                    <CodedClinicalObservations
                        setData={setObservations}
                        patient={props.patient}
                        checkLenght={validation}
                        valid={setMandatory}
                        obs={observation}
                        editObs={handleEditObs}
                    />,
            },
            {
                label: t('Clinical Findings'),
                component:
                    <CodedClinicalFindings
                        setData={setFindings}
                        patient={props.patient}
                        checkLenght={validation}
                        valid={setMandatory}
                        obs={observation}
                        editObs={handleEditObs}
                        modifyPatient={props.modifyPatient}
                    />,
            },
            {
                label: t('Injury Characteristics'),
                component:
                    <Injury
                        setData={setInjury}
                        patient={props.patient}
                        checkLenght={validation}
                        valid={setMandatory}
                        ecds
                    />,
            },
            {
                label: t('Patient Clinical History'),
                component:
                    <PatientHistory
                        setData={setHistory} patient={props.patient}
                        checkLenght={validation}
                        valid={setMandatory}
                    />,
            },
            {
                label: t('Service Agreement Details'),
                component:
                    <AgreementServiceDetails
                        setData={setAgreement}
                        patient={props.patient}
                        checkLenght={validation}
                        valid={setMandatory}
                        isSmall={isSmall}
                    />,
            },
            {
                label: t('Care Professionals'),
                component:
                    <CareProfessionals
                        setData={setCare}
                        patient={props.patient}
                        checkLenght={validation}
                        valid={setMandatory}
                        ecdsData
                        isSmall={isSmall}
                    />,
            },
            {
                label: t(version === 'V3'
                    ? 'Emergency Care Diagnosis'
                    : 'Urgent And Emergency Care Diagnosis'),
                component:
                    <Diagnosis
                        setData={setDiagnosis}
                        patient={props.patient}
                        checkLenght={validation}
                        valid={setMandatory}
                    />,
            },
            {
                label: t(version === 'V3'
                    ? 'Emergency Care Investigations'
                    : 'Urgent And Emergency Care Investigations'),
                component:
                    <Investigations
                        setData={setInvestigations}
                        patient={props.patient}
                        checkLenght={validation}
                        valid={setMandatory}
                    />,
            },
            {
                label: t(version === 'V3'
                    ? 'Emergency Care Treatments'
                    : 'Urgent And Emergency Care Treatments'),
                component:
                    <Treatments
                        setData={setTreatments}
                        patient={props.patient}
                        checkLenght={validation}
                        valid={setMandatory}
                    />,
            },
            {
                label: t('Referrals to Other Services'),
                component:
                    <ReferralsOtherServices
                        setData={setOtherService}
                        patient={props.patient}
                        checkLenght={validation}
                        valid={setMandatory}
                        ecdsData
                    />,
            },
            {
                label: t('Discharge from Emergency Care'),
                component:
                    <DischargeECDS
                        setData={setDischarge}
                        patient={props.patient}
                        checkLenght={validation}
                        valid={setMandatory}
                    />,
            },
            {
                label: t('Research and Disease Outbreak Notification'),
                component:
                    <DiseaseOutbreak
                        setData={setOutbreak}
                        patient={props.patient}
                        checkLenght={validation}
                        valid={setMandatory}
                        ecdsForm
                    />,
            },
        ];

        if (version === 'V3') {
            accordionSteps.unshift(
                {
                    label: t('Patient Pathway'),
                    component:
                        <Pathway
                            setData={setPatientPathway}
                            patient={props.patient}
                            checkLenght={validation}
                            valid={setMandatory}
                        />,
                },
            );
        } else if (version === 'V4') {
            accordionSteps.splice(2, 0,
                {
                    label: t('Social And Personal Circumstances'),
                    component:
                        <SocialAndPersonalCircumstances
                            setData={setSocial}
                            patient={props.patient}
                            checkLenght={validation}
                            valid={setMandatory}
                            ecdsData
                        />,
                },
            );
            accordionSteps.splice(20, 0,
                {
                    label: t('eMed 3 Fit Note'),
                    component:
                        <EMed3ECDS
                            setData={setEMed}
                            patient={props.patient}
                            checkLenght={validation}
                            valid={setMandatory}
                            ecdsData
                        />,
                },
            );
        }
        setSteps(accordionSteps);
    };

    const updateObs = async () => {
        API.getFullObservations(props.patient, 'news', true, controller.signal)
            .then((result) => {
                // Fetch data to table
                if (!_.isEmpty(result)) {
                    setObservation(result);
                }
            });
    };

    const crateEcdsData = () => {
        if (versionECDS === 'V3') {
            return {
                PatientPathway: patientPathway,
                PersonGroupPatient: {
                    PatientIdentity: patientIdentity,
                    PatientCharacteristics_EmergencyCare: patientCharacteristics,
                    MentalHealthActLegalStatus: mentalHealth,
                },
                GPRegistration: gp,
                EmergencyCareAttendanceLocation: attendanceLocation,
                AttendanceOccurrence: {
                    AmbulanceDetails: ambulanceDetails,
                    ExpectedDateTimeOfTreatment: timeOfTreatment,
                    EmergencyCareAttendanceActivityCharacteristics: attendanceActivity,
                    CodedScoredAssessment: assessment,
                    CodedClinicalObservations: observations,
                    CodedClinicalFinding: findings,
                    InjuryCharacteristics: injury,
                    PatientClinicalHistory: history,
                    ServiceAgreementDetails: agreement,
                    CareProfessionals_EmergencyCare: care?.CareProfessionals_EmergencyCare,
                    EmergencyCareDiagnoses_SnomedCt: diagnosis?.EmergencyCareDiagnoses_SnomedCt,
                    EmergencyCareInvestigations_SnomedCt: investigations?.EmergencyCareInvestigations_SnomedCt,
                    EmergencyCareTreatments_SnomedCt: treatments?.EmergencyCareTreatments_SnomedCt,
                    ReferralsToOtherServices: otherService?.ReferralsToOtherServices,
                    DischargeFromEmergencyCare: discharge,
                    ResearchAndDiseaseOutbreakNotification: outbreak,
                },
            };
        } else if (versionECDS === 'V4') {
            return {
                PersonGroupPatient: {
                    PatientIdentity: patientIdentity,
                    PatientCharacteristics_UrgentAndEmergencyCare: patientCharacteristics,
                    PatientCharacteristics_UrgentAndEmergencyCare_SocialAndPersonalCircumstances_SnomedCT: social,
                    MentalHealthActLegalStatus: mentalHealth,
                },
                GPRegistration: gp,
                UrgentAndEmergencyCareActivityLocation: {
                    OrganisationSiteIdentifier_OfTreatment: attendanceLocation,
                    UrgentAndEmergencyCareActivityType: attendanceLocation,
                },
                AttendanceOccurrence: {
                    AmbulanceDetails: ambulanceDetails,
                    ExpectedDateTimeOfTreatment: timeOfTreatment,
                    UrgentAndEmergencyCareActivityCharacteristics: attendanceActivity,
                    CodedScoredAssessment: assessment,
                    CodedClinicalObservations: observations,
                    CodedClinicalFinding: findings,
                    InjuryCharacteristics: injury,
                    PatientClinicalHistory: history,
                    ServiceAgreementDetails: agreement,
                    CareProfessionals_UrgentAndEmergencyCare: care?.CareProfessionals_EmergencyCare,
                    UrgentAndEmergencyCareDiagnoses_SnomedCt: diagnosis?.EmergencyCareDiagnoses_SnomedCt,
                    UrgentAndEmergencyCareInvestigations_SnomedCt: investigations?.EmergencyCareInvestigations_SnomedCt,
                    UrgentAndEmergencyCareTreatments_SnomedCt: treatments?.EmergencyCareTreatments_SnomedCt,
                    ReferralsToOtherServices: otherService?.ReferralsToOtherServices,
                    EMED3_FitNote: eMed,
                    DischargeFromEmergencyCare: discharge,
                    ResearchAndDiseaseOutbreakNotification: outbreak,
                },
            };
        }
    };

    const createObject = (date) => {
        let checkCreate;

        if (props.patient?.studies?.EmergencyCare?.created)
            checkCreate = props.patient?.studies.EmergencyCare.created;
        let ecdsData = crateEcdsData();
        ecdsData.created = {
            by: checkCreate?.by ? checkCreate?.by : tester,
            login: checkCreate?.login ? checkCreate?.login : userObject?.userName,
            date: checkCreate?.date ? checkCreate?.date : date,
            lastModifyBy: checkCreate?.by ? tester : null,
            lastModifyLogin: checkCreate?.lastModifyLogin ? userObject?.userName : null,
            lastModifyDate: checkCreate?.date ? date : null,
        };
        ecdsData.version = versionECDS;
        return ecdsData;
    };

    const checkMandatoryFields = () => {
        let check = true;

        if (config?.modules?.forms?.ecds?.validation) {
            _.forEach(mandatory, element => {
                if (!check) return; // Break the loop

                if (element?.groupStatus !== 'M') {
                    if (element?.touch && element?.mandatory) {
                        dispatch(setSnackbar({
                            type: 'warning',
                            message: `Not all mandatory fields in optional section "${element.name}" have been filled out.`,
                        }));
                        check = false;
                    } else {
                        check = true;
                    }
                } else {
                    if (element?.mandatory) {
                        dispatch(setSnackbar({
                            type: 'warning',
                            message: `Not all mandatory fields in "${element.name}" have been filled out.`,
                        }));
                        check = false;
                    }
                }
                if (element?.errors && check) {
                    dispatch(setSnackbar({
                        type: 'warning',
                        message: `There was a validation error in some field in section "${element.name}".`,
                    }));
                    check = false;
                }
                if (element?.pickers && check) {
                    dispatch(setSnackbar({
                        type: 'warning',
                        message: `There was a validation error in date/time pickers in section "${element.name}".`,
                    }));
                    check = false;
                }
            });
        }
        return check;
    };

    const getDict = (dict) => {
        return dispatch(getDictForECDS(dict));
    };

    const getNote = () => {
        return Notes.getECDSNote(
            patientPathway,
            patientIdentity,
            patientCharacteristics,
            mentalHealth,
            gp,
            attendanceLocation,
            ambulanceDetails,
            attendanceActivity,
            timeOfTreatment,
            assessment,
            observations,
            findings,
            history,
            injury,
            agreement,
            care,
            diagnosis,
            investigations,
            treatments,
            otherService,
            discharge,
            outbreak,
            social,
            eMed,
            props.patient?.patient?.nhsNumberVerifiedStatus,
            getDict,
            versionECDS,
        );
    };

    const submit = () => {
        if (dispatch(hasPermission(Permissions.AGYLE_IS_ECDS_ADMIN))) {
            const dateOfForm = moment().toISOString();
            if (checkMandatoryFields()) {
                if (props.disableSave) {
                    const ecdsObj = createObject(dateOfForm);

                    API.updatePatientInWard(props.patient.__id, [{
                            'studies.EmergencyCare': ecdsObj,
                        }])
                        .then((response) => {
                            if (response.data) {
                                dispatch(addNoteForPatient(props.patient.patient.__id, {
                                    note: getNote(),
                                    noteType: 'studies.EmergencyCare',
                                    patientsInWardsRef: [props.patient.__id],
                                    episodeId: props.patient?.admission?.consultantEpisodeId || null,
                                }, props.patient.__id));
                                if (props.modifyPatient) {
                                    props.modifyPatient([{
                                        name: 'studies.EmergencyCare',
                                        value: ecdsObj,
                                    }]);
                                }
                            }
                        });
                } else {
                    props.onChange([{
                        name: 'studies.EmergencyCare',
                        value: createObject(dateOfForm),
                        noteToAdd: getNote(),
                    }]);
                }
            } else {
                return false;
            }
        }
    };

    const accordionChangeHandler = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const handleCloseObsEditor = () => {
        setSelectedObs(null);
        closeObsEditor();
    };

    const render = useCallback(() => {
        return steps.map((item) => (
            <AgyleAccordion
                key={item.label}
                expanded={expanded === item.label}
                onChange={accordionChangeHandler(item.label)}
                label={t(item.label)}
                unmountOnExit={false}
                formBackground
            >
                {React.cloneElement(item.component, {accordion: expanded, version: versionECDS})}
            </AgyleAccordion>
        ));
    }, [expanded, observation, steps]);

    return (
        <>
            <div className={classes.body}>
                {render()}

                <FormLabel className={classes.ecdsVersion}>
                    {t('Used ECDS version')}: {versionECDS}
                </FormLabel>
            </div>

            {(!!selectedObs && isObsEditorOpen) &&
                <ObsTestEdit
                    open={isObsEditorOpen}
                    patient={props.patient}
                    onClose={handleCloseObsEditor}
                    selectedObs={selectedObs}
                    observations={observation}
                    refreshObs={updateObs}
                />
            }
        </>
    );
};

export default EcdsForm;
